import { useState } from "react";
import { Button, LinkButton, QuietButton, SecondaryButton } from "../ui/button";
import { Screen } from "../ui/screen";
import { groupDiff, hasChanges } from "../../lib/rules";
import type { DiffLine, RulesConflict, RulesWarning } from "../../lib/api";

type Props = {
  lines: DiffLine[];
  conflicts: RulesConflict[];
  /**
   * What this document cannot do (ruling R-0910-4). Server-computed, shown
   * directly above Accept, and never a gate on it: the owner is told what a
   * missing section costs and then decides, exactly as with a conflict.
   */
  warnings: RulesWarning[];
  /** The manual path can go back to the textarea; a spoken instruction cannot. */
  canKeepEditing: boolean;
  currentVersion: number;
  busy: boolean;
  error: string | null;
  errorLine: string | null;
  onAccept: () => void;
  onKeepEditing: () => void;
  onDiscard: () => void;
  /**
   * Loads the document the wizard composed and proposes it (R-0910-4,
   * amended). A way back for an owner who deleted the sections and read, in
   * the warnings above, what that costs.
   */
  onRestoreDefault: () => void;
};

/** The one line above the diff when the heuristic has something to say. */
export const ADVISORY_LEAD = "These lines may pull in opposite directions:";

/**
 * The review surface (PRD FR-2.5, FR-8). Every path into the editor — typing,
 * or telling Nob — arrives here, and nothing is written until it is accepted.
 * Where the contradiction heuristic has flagged a pair, the pair is shown above
 * the diff as a note and nothing else changes (ruling R-0905-6): Accept is the
 * same plain Accept, because the heuristic advises and the owner decides.
 */
export function DiffView({
  lines,
  conflicts,
  warnings,
  canKeepEditing,
  currentVersion,
  busy,
  error,
  errorLine,
  onAccept,
  onKeepEditing,
  onDiscard,
  onRestoreDefault,
}: Props) {
  const [whole, setWhole] = useState(false);
  const changed = hasChanges(lines);
  const segments = whole
    ? lines.map((line, index) => ({ kind: "line" as const, index, line }))
    : groupDiff(lines);

  return (
    <Screen wide>
      <p className="text-[13px] uppercase tracking-[0.18em] text-faint">Proposed change</p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight text-text sm:text-4xl">
        Nothing is saved yet.
      </h1>
      <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-muted">
        {changed
          ? `Accepting writes version ${currentVersion + 1}. Discarding leaves version ${currentVersion} exactly as it is.`
          : "Nob read it and found nothing to change. Version " +
            `${currentVersion} stands as it is.`}
      </p>

      {conflicts.length > 0 && (
        <div className="mt-6">
          <AdvisoryNote conflicts={conflicts} />
        </div>
      )}

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-surface/30">
        {/* Scrollable, so it is focusable: a long diff must be readable by keyboard. */}
        <div
          tabIndex={0}
          role="group"
          aria-label="The proposed change, line by line"
          className="max-h-[60vh] overflow-y-auto py-4 focus-visible:outline-none
                     focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus/40"
        >
          {segments.map((segment, i) =>
            segment.kind === "gap" ? (
              <button
                key={`gap-${i}`}
                type="button"
                onClick={() => setWhole(true)}
                className="block w-full px-5 py-2 text-left font-mono text-[11px] uppercase tracking-[0.14em]
                           text-fainter transition-colors duration-75 hover:text-muted
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
              >
                {segment.count} unchanged {segment.count === 1 ? "line" : "lines"}
              </button>
            ) : (
              <DiffRow key={`line-${segment.index}`} line={segment.line} />
            ),
          )}
        </div>
      </div>

      {error && (
        <div className="mt-6" role="alert">
          <p className="text-sm text-danger">{error}</p>
          {errorLine && (
            <p className="mt-2 rounded-lg border border-border bg-surface/60 px-4 py-3 font-mono text-[13px] text-muted">
              {errorLine}
            </p>
          )}
        </div>
      )}

      {warnings.length > 0 && (
        <ul data-testid="rules-warnings" className="mt-6 space-y-2">
          {warnings.map((warning, i) => (
            <li key={i} className="text-sm leading-relaxed text-warn">
              {warning.text}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-2">
        {changed && (
          <Button type="button" onClick={onAccept} disabled={busy}>
            Accept
          </Button>
        )}
        {canKeepEditing && (
          <SecondaryButton type="button" onClick={onKeepEditing} disabled={busy}>
            Keep editing
          </SecondaryButton>
        )}
        <QuietButton type="button" onClick={onDiscard} disabled={busy}>
          Discard
        </QuietButton>
        {/* Quiet, beside Discard: a way back, not a route anybody is pushed
            down. It proposes rather than writes, so it lands on this same
            screen with a diff of its own. */}
        <QuietButton type="button" onClick={onRestoreDefault} disabled={busy}>
          Restore default
        </QuietButton>
      </div>

      {whole && (
        <p className="mt-6">
          <LinkButton type="button" onClick={() => setWhole(false)}>
            Show the change only
          </LinkButton>
        </p>
      )}
    </Screen>
  );
}

const ROW: Record<DiffLine["type"], string> = {
  same: "text-muted",
  add: "bg-accent/[0.07] text-accent-ink-strong",
  del: "text-fainter line-through decoration-border-strong",
};

const GUTTER: Record<DiffLine["type"], string> = {
  same: "text-transparent",
  add: "text-accent-ink/80",
  del: "text-fainter",
};

const MARK: Record<DiffLine["type"], string> = { same: " ", add: "+", del: "−" };

/**
 * One line of the document, marked in the gutter rather than by colour alone.
 * Additions carry the door's amber; deletions are struck and dimmed. Exported
 * so the surfaced-proposal card (M15) renders its diff identically (FR-8.3).
 */
export function DiffRow({ line }: { line: DiffLine }) {
  return (
    <div
      data-diff={line.type}
      className={`nob-diff nob-diff-${line.type} grid grid-cols-[1.75rem_1fr] px-2 font-mono text-[13px] leading-6 ${ROW[line.type]}`}
    >
      <span aria-hidden className={`select-none text-center ${GUTTER[line.type]}`}>
        {MARK[line.type]}
      </span>
      <span className="min-h-6 whitespace-pre-wrap break-words pr-3">{line.line}</span>
    </div>
  );
}

/**
 * The advisory (ruling R-0905-6): one lead line and the pairs, in the gutter
 * voice. No buttons, no choice to make, no gate — a keyword heuristic flagged
 * these, and it is wrong often enough that all it may do is point. Exported so
 * a surfaced proposal (M15) shows the same note (FR-8.3).
 */
export function AdvisoryNote({ conflicts }: { conflicts: RulesConflict[] }) {
  return (
    <aside
      data-testid="conflict-advisory"
      className="rounded-xl border border-accent/25 bg-accent/[0.04] px-5 py-4"
    >
      <p className="text-[15px] leading-relaxed text-text">{ADVISORY_LEAD}</p>
      <ul className="mt-3 space-y-3">
        {conflicts.map((conflict, i) => (
          <li key={i} className="font-mono text-[13px] leading-6">
            <p className="text-text-soft">{conflict.existing_line}</p>
            <p className="text-accent-ink-strong">{conflict.proposed_line}</p>
          </li>
        ))}
      </ul>
    </aside>
  );
}
